import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { updateMeeting, getAllChapters, getListOfExcos } from '../../utils/api-calls';
import styled from "styled-components";
import { rel8Pink, rel8Purple, rel8White } from "../../globals";
import { mobile } from "../../responsive";
import Loading from "../Loading/Loading";

const BackDrop = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const SubCon = styled.div`
  background-color: ${rel8White};
  width: 350px;
  max-height: 80%;
  border-radius: 10px;
  padding: 20px;
  overflow-y: auto;

  ${mobile({
    width: "250px",
  })}
`;

const SubConHeader = styled.p`
  font-weight: 700;
  text-align: center;
`;

const Form = styled.form`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: ${props => props.flexDirection || 'column'};
  font-size: 12px;
  margin: 10px 0px;
`;

const FormDataComp = styled.input`
  padding: 5px 0px;
  background-color: transparent;
  border: none;
  border: 1px solid ${rel8Purple};
  border-radius: 5px;
  padding: 5px;
  color: ${rel8Purple};
  outline: none;
  &::placeholder {
    color: ${rel8Purple};
  }
`;

const FormSelection = styled.select`
  padding: 5px 0px;
  color: ${rel8Purple};
  outline: none;
  border: none;
  border-bottom: 1px solid ${rel8Purple};
  margin: 10px 0px;
  overflow: auto;
`;

const FormOption = styled.option``;

const FormTextArea = styled.textarea`
  padding: 5px 0px;
  background-color: transparent;
  border: none;
  border: 1px solid ${rel8Purple};
  border-radius: 5px;
  padding: 5px;
  color: ${rel8Purple};
  outline: none;
  &::placeholder {
    color: ${rel8Purple};
  }
`;

const SubConBtnHold = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 20px;
`;

const SubConBtn = styled.input`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: ${(props) =>
    props.typex === "filled" ? `${rel8Purple}` : `${rel8Pink}`};
  color: ${(props) =>
    props.typex === "filled" ? `${rel8White}` : `${rel8Purple}`};
  cursor: pointer;
`;

const EditMeetingModal = ({ show, onClose, data, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: data?.name || '',
    details: data?.details || '',
    url: data?.url || '',
    organiserName: data?.organiserName || '',
    organiserDetails: data?.organiserDetails || '',
    exco: data?.exco || '',
    chapters: data?.chapters || '',
    date_for: data?.date_for || '',
    addresse: data?.addresse || '',
    event_date: data?.event_date || ''
  });

  const [isVirtual, setIsVirtual] = useState(data?.url ? true : false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        details: data.details || '',
        url: data.url || '',
        organiserName: data.organiserName || '',
        organiserDetails: data.organiserDetails || '',
        exco: data.exco || '',
        chapters: data.chapters || '',
        date_for: data.date_for,
        addresse: data.addresse || '',
        event_date: data.event_date
      });
      setIsVirtual(data?.url ? true : false);
    }
  }, [data]);

  const { isLoading, isFetching, isError, data: chaptersData } = useQuery(
    "all-chapters",
    getAllChapters,
    {
      refetchOnWindowFocus: false,
      select: (data) => data.results,
    }
  );

  const {
    isLoading: excoLoading,
    isFetching: excoFetching,
    isError: excoError,
    data: excoData,
  } = useQuery("all-excos", getListOfExcos, {
    refetchOnWindowFocus: false,
    select: (data) =>
      data.data.map((item) => ({ id: item.id, name: item.name })),
  });

  const { isLoading: updateLoading, mutate: updateMeetingMutation } = useMutation(
    (meetingData) => updateMeeting(meetingData.id, meetingData.data),
    {
      onMutate: () => {
        toast.info("Meeting update in progress", {
          progressClassName: "toastProgress",
          icon: false,
        });
      },
      onSuccess: (response) => {
        toast.success("Meeting updated successfully", {
          progressClassName: "toastProgress",
          icon: false,
        });
        onSubmit(response);
        onClose();
      },
      onError: (error) => {
        toast.error("Could not update meeting");
        if (error?.message?.response?.data?.message?.error) {
          toast.error(`Message: ${error.message.response.data.message.error}`, {
            autoClose: 9000,
          });
        }
      },
    }
  );

  const formatForDatetimeLocal = (dateString) => {
    const date = new Date(dateString);
    // Convert to local timezone and format as YYYY-MM-DDTHH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isVirtual') {
      setIsVirtual(checked);
      if (!checked) {
        setFormData(prev => ({ ...prev, url: '' }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create payload, excluding URL if not virtual
    const payload = { ...formData };
    if (!isVirtual) {
      delete payload.url;
    }

    updateMeetingMutation({ id: data.id, data: payload });
  };

  if (!show) return null;

  return (
    <BackDrop>
      <style>
        {`
          body {
            overflow: hidden;
          }
        `}
      </style>
      {isLoading || isFetching || excoLoading || excoFetching ? (
        <Loading
          loading={isLoading || isFetching || excoLoading || excoFetching}
        />
      ) : !isError && !excoError ? (
        <SubCon>
          <SubConHeader>Edit Meeting</SubConHeader>
          <Form onSubmit={handleSubmit}>
            <FormLabel>
              Name:
              <FormDataComp
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel>
              Details:
              <FormTextArea
                name="details"
                value={formData.details}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel flexDirection="row">
              Is Virtual:
              <FormDataComp
                type="checkbox"
                name="isVirtual"
                onChange={handleChange}
                checked={isVirtual}
                style={{ marginLeft: '12px' }}
              />
            </FormLabel>

            {isVirtual && (
              <FormLabel>
                Meeting URL:
                <FormDataComp
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </FormLabel>
            )}

            <FormLabel>
              Organiser Name:
              <FormDataComp
                type="text"
                name="organiserName"
                value={formData.organiserName}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel>
              Organiser Details:
              <FormDataComp
                type="text"
                name="organiserDetails"
                value={formData.organiserDetails}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel>
              Exco:
              <FormSelection
                name="exco"
                value={formData.exco}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                {excoData?.map((item) => (
                  <FormOption key={item.id} value={item.id}>
                    {item.id} || {item.name}
                  </FormOption>
                ))}
              </FormSelection>
            </FormLabel>

            <FormLabel>
              Chapter:
              <FormSelection
                name="chapters"
                value={formData.chapters}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                {chaptersData?.map((item) => (
                  <FormOption key={item.id} value={item.id}>
                    {item.id} || {item.name}
                  </FormOption>
                ))}
              </FormSelection>
            </FormLabel>

            <FormLabel>
              Date For:
              <FormDataComp
                type="datetime-local"
                name="date_for"
                value={formData.date_for ? formatForDatetimeLocal(formData.date_for) : ''}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel>
              Address:
              <FormDataComp
                type="text"
                name="addresse"
                value={formData.addresse}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <FormLabel>
              Event Date:
              <FormDataComp
                type="datetime-local"
                name="event_date"
                value={formData.event_date ? formatForDatetimeLocal(formData.event_date) : ''}
                onChange={handleChange}
                required
              />
            </FormLabel>

            <SubConBtnHold>
              <SubConBtn 
                type="submit" 
                value="Save" 
                typex="filled" 
                disabled={updateLoading} 
              />
              <SubConBtn 
                type="button" 
                value="Cancel" 
                onClick={onClose} 
              />
            </SubConBtnHold>
          </Form>
        </SubCon>
      ) : (
        <small style={{ color: "white", fontSize: "20px" }}>
          An error occurred while fetching data. Kindly refresh page.
        </small>
      )}
    </BackDrop>
  );
};

export default EditMeetingModal;